import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuthContext } from 'context/AuthContext';
import Header from 'components/Header';
import Button from 'components/Button';
import Popup from 'components/Popup';
import TextInput from 'components/TextInput';
import Dropdown from 'components/Dropdown';
import Table from 'components/Table';
import axios from 'config/axios';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import './index.css';

const GENDERS = [
    'Male',
    'Female',
    'Prefer not to say'
];
const ACCOMODATIONS = {
    'None': 1,
    '50% Extra': 1.5,
    '100% Extra': 2
};

const CLASS_YEARS = [...Array(20)].map((_, i) => new Date().getFullYear() + 5 - i);

function Students() {
    const [students, setStudents] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [search, setSearch] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [success, setSuccess] = useState('');
    const [failed, setFailed] = useState('');
    const [organization, setOrganization] = useState(null);
    const [email, setEmail] = useState('');
    const [studentId, setStudentId] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [gender, setGender] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [school, setSchool] = useState('');
    const [classOf, setClassOf] = useState('');
    const [accomodation, setAccomodation] = useState(1);
    // Selection is keyed by StudentOrganization id, never by row index, so
    // sorting, paging and searching can't retarget it.
    const [selectedIds, setSelectedIds] = useState(new Set<number>());
    const [confirmDeactivate, setConfirmDeactivate] = useState(false);

    const { user } = useAuthContext();

    const canBulk = user.role === 'Admin' || user.role === 'Super Admin';

    const closeForm = () => {
        setFormOpen(false);
        setOrganization(organizations[0]);
        setEmail('');
        setStudentId('');
        setFirstName('');
        setLastName('');
        setPassword('');
        setPhoneNumber('');
        setPostalCode('');
        setGender('');
        setBirthDate('');
        setSchool('');
        setClassOf('');
        setAccomodation(1);
    }

    const navigate = useNavigate();
    const addStudentRef = useOutsideClickHandler(closeForm);
    const successRef = useOutsideClickHandler(() => setSuccess(''));
    const failedRef = useOutsideClickHandler(() => setFailed(''));
    const deactivateRef = useOutsideClickHandler(() => setConfirmDeactivate(false));

    const getStudents = () => {
        axios.get('tutors/students/')
            .then(res => {
                setSelectedIds(new Set<number>());
                setStudents(res.data.students);
                setOrganizations(res.data.organizations);
                if (res.data.organizations.length > 0) {
                    setOrganization(res.data.organizations[0]);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    const addStudent = (confirmSignOut = false) => {
        if (!firstName || !lastName || !email || !password) {
            setFailed('Please make sure you have filled in all the required fields.');
        }
        else if (password.length < 6) {
            setFailed('Password must be at least 6 characters long.');
        }
        else {
            axios.post('tutors/students/add/', {
                    organization: organization?.id,
                    email: email,
                    studentId: studentId,
                    firstName: firstName,
                    lastName: lastName,
                    password: password,
                    countryCode: null,
                    phoneNumber: phoneNumber,
                    postalCode: postalCode,
                    gender: gender,
                    birthDate: birthDate,
                    highSchool: school,
                    classOf: classOf,
                    accomodation: accomodation,
                    confirmSignOut: confirmSignOut
                })
                .then(res => {
                    closeForm();
                    setSuccess('Successfully added student!');
                    getStudents();
                })
                .catch(error => {
                    console.log(error);
                    // Adding a student who already has an account resets their
                    // password, which logs them out -- the server asks first if
                    // they're taking a test right now.
                    if (error.response?.status === 409 && error.response.data?.code === 'student_in_test') {
                        const testName = error.response.data.testName || 'a test';
                        if (window.confirm(`${firstName} is taking ${testName} right now. Adding them resets their password and will log them out, and answers in the current module that haven't been submitted may be lost. Add anyway?`)) {
                            addStudent(true);
                        }
                        return;
                    }
                    const message = error.response?.data?.message;
                    if (message) {
                        setFailed(message);
                    } else if (error.response?.status === 403) {
                        setFailed('Failed to add student. Student with email already exists.');
                    } else {
                        setFailed('Failed to add student. Please make sure you have filled in all the fields correctly.');
                    }
                });
        }
    }

    useEffect(() => {
        getStudents();
    }, []);

    // Single source of truth for the visible rows. The search predicate used to
    // be written out verbatim for each Table prop; every prop below derives from
    // this instead. Active rows lead by default -- Table renders `rows` in the
    // given order until a header is clicked, so ordering here is what shows on
    // first paint.
    const filteredStudents = useMemo(() => {
        const term = search.toLowerCase();
        return students
            .filter(student => (
                !search ||
                student.email?.toLowerCase().includes(term) ||
                student.studentId?.toLowerCase().includes(term) ||
                student.firstName?.toLowerCase().includes(term) ||
                student.lastName?.toLowerCase().includes(term)
            ))
            .sort((a, b) => Number(b.isActive) - Number(a.isActive));
    }, [students, search]);

    const toggleStudent = (id: number) => {
        setSelectedIds(previous => {
            const next = new Set(previous);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }

    // Applies to the page on screen, not to everything the search matches.
    // Table owns pagination, so it passes the source indices of the rows it is
    // currently showing; they index into the same array the `rows` prop was
    // built from.
    const toggleAllOnPage = (pageSourceIndices: number[]) => {
        const pageStudents = pageSourceIndices
            .map(index => filteredStudents[index])
            .filter(Boolean);
        const allOnPageSelected = pageStudents.length > 0
            && pageStudents.every(student => selectedIds.has(student.id));

        setSelectedIds(previous => {
            const next = new Set(previous);
            if (allOnPageSelected) {
                pageStudents.forEach(student => next.delete(student.id));
            } else {
                pageStudents.forEach(student => next.add(student.id));
            }
            return next;
        });
    }

    const selectedStudents = students.filter(student => selectedIds.has(student.id));
    const selectedActive = selectedStudents.filter(student => student.isActive);
    const selectedInactive = selectedStudents.filter(student => !student.isActive);

    // A mixed selection is fine: each button applies only to its own subset.
    const setActive = (ids: number[], isActive: boolean) => {
        axios.post('tutors/students/setActive/', { ids: ids, isActive: isActive })
            .then(res => {
                setConfirmDeactivate(false);
                const skipped = res.data.skipped?.length || 0;
                setSuccess(skipped
                    ? `${res.data.updated} student${res.data.updated === 1 ? '' : 's'} ${isActive ? 'reactivated' : 'deactivated'}. ${skipped} skipped -- outside your organizations.`
                    : `${res.data.updated} student${res.data.updated === 1 ? '' : 's'} ${isActive ? 'reactivated' : 'deactivated'}.`
                );
                getStudents();
            })
            .catch(error => {
                console.log(error);
                setConfirmDeactivate(false);
                setFailed(`Failed to ${isActive ? 'reactivate' : 'deactivate'} students.`);
            });
    }

    const newStudent = (
        <Popup
            title='New student'
            primaryButtonText='Save'
            primaryButtonOnClick={() => addStudent(false)}
            secondaryButtonText='Cancel'
            secondaryButtonOnClick={closeForm}
            innerRef={addStudentRef}
        >
            <Dropdown
                label='Organization'
                data={organizations}
                dataKey='name'
                placeholder='select organization'
                value={organization?.name}
                onSelect={value => setOrganization(value)}
                required
            />
            <div className='students__add'>
                <TextInput
                    label='First name'
                    placeholder='enter first name'
                    value={firstName}
                    onChange={event => setFirstName(event.target.value)}
                    required
                />
                <TextInput
                    label='Last name'
                    placeholder='enter last name'
                    value={lastName}
                    onChange={event => setLastName(event.target.value)}
                    required
                />
                <TextInput
                    label='Email'
                    placeholder='enter email'
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    type='email'
                    required
                />
                <TextInput
                    label='Password'
                    placeholder='enter password'
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    required
                />
                <TextInput
                    label='Phone number'
                    placeholder='0000000000'
                    value={phoneNumber}
                    onChange={event => {
                        const value = event.target.value.replace(/[^\d]/g, '');
                        if (value.length <= 15) setPhoneNumber(value);
                    }}
                    type='text'
                />
                <TextInput
                    label='Zip Code'
                    placeholder='12345'
                    value={postalCode}
                    onChange={event => setPostalCode(event.target.value)}
                />
                <Dropdown
                    label='Gender'
                    data={GENDERS}
                    placeholder='select gender'
                    value={gender}
                    onSelect={value => setGender(value)}
                />
                <TextInput
                    label='Date of birth'
                    value={birthDate}
                    onChange={event => setBirthDate(event.target.value)}
                    type='date'
                />
                <TextInput
                    label='School'
                    placeholder='enter school name'
                    value={school}
                    onChange={event => setSchool(event.target.value)}
                />
                <Dropdown
                    label='Class of'
                    data={CLASS_YEARS}
                    placeholder='select class year'
                    value={classOf}
                    onSelect={value => setClassOf(value)}
                />
                <TextInput
                    label='Student ID'
                    placeholder='enter student ID'
                    value={studentId}
                    onChange={event => setStudentId(event.target.value)}
                />
                <Dropdown
                    label='Time Extension'
                    data={Object.keys(ACCOMODATIONS)}
                    onSelect={(value) => setAccomodation(ACCOMODATIONS[value])}
                    value={Object.keys(ACCOMODATIONS).find(key => ACCOMODATIONS[key] == accomodation)}
                />
            </div>
        </Popup>
    );

    const successMessage = (
        <Popup
            title='Success'
            primaryButtonText='Close'
            primaryButtonOnClick={() => setSuccess('')}
            innerRef={successRef}
        >
            {success}
        </Popup>
    );

    const deactivatePopup = (
        <Popup
            title='Deactivate students'
            primaryButtonText='Deactivate'
            primaryButtonOnClick={() => setActive(selectedActive.map(student => student.id), false)}
            secondaryButtonText='Cancel'
            secondaryButtonOnClick={() => setConfirmDeactivate(false)}
            innerRef={deactivateRef}
        >
            <p>
                {selectedActive.length} student{selectedActive.length === 1 ? '' : 's'} will be
                deactivated. They will no longer be able to sign in, and they lose access to
                any tests assigned to them by your organization.
            </p>
            <p>
                Their account and past results are kept, and you can reactivate them from this
                list at any time.
            </p>
        </Popup>
    );

    const failedMessage = (
        <Popup
            title='Failed'
            primaryButtonText='Close'
            primaryButtonOnClick={() => setFailed('')}
            innerRef={failedRef}
        >
            {failed}
        </Popup>
    );

    const downloadCSV = () => {
        axios({
            url: 'tutors/generate_csv/',
            method: 'GET',
            responseType: 'blob', // 데이터 형식으로 blob을 설정
        })
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'student_data.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        })
        .catch(error => {
            console.error('Error downloading CSV:', error);
        });
    }

    return (
        <div className='students'>
            <Helmet>
                <title>
                    All Students - Test Center Admin
                </title>
            </Helmet>
            <Header title='All Students' />
            {failed
                ? failedMessage
                : success
                ? successMessage
                : formOpen
                ? newStudent
                : confirmDeactivate
                ? deactivatePopup
                : <></>
            }
            <div className='students__bar'>
                <TextInput
                    className='students__search'
                    placeholder='search student'
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                />
                {canBulk &&
                    <div className='students__buttons'>
                        {selectedIds.size > 0 && <>
                            <Button
                                secondary
                                disabled={selectedActive.length === 0}
                                onClick={() => setConfirmDeactivate(true)}
                            >
                                Deactivate ({selectedActive.length})
                            </Button>
                            <Button
                                secondary
                                disabled={selectedInactive.length === 0}
                                onClick={() => setActive(selectedInactive.map(student => student.id), true)}
                            >
                                Reactivate ({selectedInactive.length})
                            </Button>
                        </>}
                        <Button onClick={() => downloadCSV()}>
                            Export
                        </Button>
                        <Button onClick={() => setFormOpen(!formOpen)}>
                            New student
                        </Button>
                    </div>
                }
            </div>
            {/*
                The Status cell is JSX, and Table's string sort calls .toString()
                on the cell -- which yields "[object Object]" for a React element.
                sortValues supplies plain values instead, and is all-or-nothing
                per table, so every column is listed there.
            */}
            <Table 
                header={['ID', 'Name', 'Student Email', 'Organization', 'Status', 'Registered Date']}
                columnTypes={['number', 'string', 'string', 'string', 'string', 'date']}   
                rows={filteredStudents.map(student => ([
                    student.studentId,
                    `${student.firstName} ${student.lastName}`,
                    student.email,
                    student.organization,
                    <span className={student.isActive
                        ? 'students__status students__status--active'
                        : 'students__status students__status--inactive'
                    }>
                        {student.isActive ? 'Active' : 'Inactive'}
                    </span>,
                    new Date(student.createdAt)
                ]))}
                sortValues={filteredStudents.map(student => ([
                    student.studentId,
                    `${student.firstName} ${student.lastName}`,
                    student.email,
                    student.organization,
                    student.isActive ? 'Active' : 'Inactive',
                    new Date(student.createdAt)
                ]))}
                onSelectCell={filteredStudents.map(student => ([
                    null,
                    () => navigate(`/students/${student.id}`),
                    null,
                    null,
                    null,
                    null
                ]))}
                selectType={canBulk ? 'checkBox' : 'row'}
                onSelectRow={canBulk
                    ? filteredStudents.map(student => () => toggleStudent(student.id))
                    : undefined
                }
                rowsSelected={canBulk
                    ? filteredStudents.map(student => selectedIds.has(student.id))
                    : undefined
                }
                onSelectAll={canBulk ? toggleAllOnPage : undefined}
            />
        </div>
    );
}

export default Students;